import {
  DataTableSortableColumn,
  TableState,
  Typography,
  ColumnDef,
  Checkbox,
} from '@ui/components';
import { useTableList } from '@shared/components/table-list/use-table-list';
import { useTranslation } from 'react-i18next';
import { useState, useMemo } from 'react';
import { TFunction } from 'i18next';

const defaultTableState: Pick<TableState, 'globalFilter' | 'pagination' | 'sorting'> = {
  sorting: [],
  pagination: {
    pageIndex: 0,
    pageSize: 4,
  },
  globalFilter: '',
};

const getReceiverColumns = (t: TFunction): ColumnDef<string>[] => [
  {
    id: 'select',
    size: 40,
    maxSize: 40,
    header: ({ table }) => (
      <Checkbox
        onCheckedChange={(value) => table.toggleAllRowsSelected(!!value)}
        checked={table.getIsAllRowsSelected()}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        checked={row.getIsSelected()}
        className="ml-0.5"
      />
    ),
  },
  {
    size: 350,
    maxSize: 350,
    header: ({ column }) => (
      <DataTableSortableColumn label={t('COST_CENTERS.DOCUMENTS_RECEIVER_EMAIL')} column={column} />
    ),
    accessorKey: 'email',
    cell: ({ row }) => (
      <Typography variant="labelMediumMedium" element="span">
        {row.original}
      </Typography>
    ),
  },
];

export const useCostCenterDocumentsForm = (recipients: string[]) => {
  const { t } = useTranslation();
  const columns = useMemo(() => getReceiverColumns(t), [t]);

  const [state, setState] = useState<Partial<TableState>>(defaultTableState);

  const data = useMemo(() => {
    const { sorting, pagination, globalFilter } = {
      ...defaultTableState,
      ...state,
    };

    if (!recipients?.length) {
      return { items: [], totalLength: 0 };
    }

    const sortDirection = sorting?.[0]?.desc ? -1 : 1;

    const searchResult = recipients.filter((email) =>
      email.toLowerCase().startsWith(globalFilter?.toLowerCase()),
    );

    const items = searchResult
      .sort((a, b) => {
        return sortDirection * a.localeCompare(b);
      })
      .slice(
        pagination.pageIndex * pagination.pageSize,
        (pagination.pageIndex + 1) * pagination.pageSize,
      );

    const totalLength = searchResult.length || 0;

    return {
      items,
      totalLength,
    };
  }, [recipients, state]);

  const tableList = useTableList({
    totalItems: data.totalLength,
    onStateChange: setState,
    getRowId: (row) => row,
    initialState: state,
    columns: columns,
    data: data.items,
  });

  return {
    tableList,
    t,
  };
};
